import { Form, Input, message } from 'antd';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import userApi from '../../apis/user';
import { ButtonType } from '../../constants/style';
import { InvoiceProfileFormValues } from '../../interfaces/Invoice';
import { PageContainer } from '../../styles/Global';
import { $border, $lightgrey, $primary } from '../../styles/Variables';
import Button from '../Button/Button';
import CustomForm from '../Form/Form';
import PageHeader from '../PageHeader/PageHeader';

const FormCard = styled.div`
  max-width: 640px;
  background: #fff;
  border: 1px solid ${$border};
  border-radius: 12px;
  padding: 28px 32px;

  .ant-form-item {
    margin-bottom: 22px;
  }

  .ant-form-item-label {
    padding-bottom: 6px;
  }
`;

const Hint = styled.p`
  color: ${$lightgrey};
  font-size: 14px;
  margin-bottom: 28px;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
`;

const InvoiceProfile = () => {
  const [form] = Form.useForm<InvoiceProfileFormValues>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const me = await userApi.getMe();
        form.setFieldsValue({
          businessName: me.businessName || '',
          fullName: me.fullName || me.name || '',
          address: me.address || '',
          invoiceEmail: me.invoiceEmail || me.email || '',
          phone: me.phone || '',
        });
      } catch (e: any) {
        message.error(e?.message || 'Failed to load invoice profile');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [form]);

  const onFinish = async (values: InvoiceProfileFormValues) => {
    setSaving(true);
    try {
      await userApi.updateInvoiceProfile({
        businessName: values.businessName?.trim() || undefined,
        fullName: values.fullName?.trim() || undefined,
        address: values.address?.trim() || undefined,
        invoiceEmail: values.invoiceEmail?.trim() || undefined,
        phone: values.phone?.trim() || undefined,
      });
      message.success('Invoice information saved');
    } catch (e: any) {
      message.error(e?.message || 'Failed to save invoice information');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader title="Invoice information" />
      <FormCard>
        <Hint>
          This information appears in the &quot;Invoice to&quot; section on
          every order invoice. Fill it in once and it will be reused for all
          invoices.
        </Hint>
        <CustomForm
          form={form}
          layout="vertical"
          onFinish={onFinish}
          disabled={loading}
        >
          <Form.Item label="Business name" name="businessName">
            <Input placeholder="Business name" />
          </Form.Item>
          <Form.Item
            label="Full name"
            name="fullName"
            rules={[{ required: true, message: 'Full name is required' }]}
          >
            <Input placeholder="Full name" />
          </Form.Item>
          <Form.Item
            label="Address"
            name="address"
            rules={[{ required: true, message: 'Address is required' }]}
          >
            <Input.TextArea rows={3} placeholder="Street, city, state, ZIP, country" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="invoiceEmail"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input placeholder="Email" />
          </Form.Item>
          <Form.Item label="Phone" name="phone">
            <Input placeholder="Phone" />
          </Form.Item>
          <Actions>
            <Button
              type={ButtonType.PRIMARY}
              htmlType="submit"
              loading={saving}
              style={{ backgroundColor: $primary }}
            >
              Save
            </Button>
          </Actions>
        </CustomForm>
      </FormCard>
    </PageContainer>
  );
};

export default InvoiceProfile;
